import { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import type { DealCandidate, DeskApi, PartnerVocabulary, RewardsConfig } from '@valutten/partner-desk-ui';
import { readFirstContactHours, readRewards, vocabularyOf } from '@valutten/partner-desk-ui';
import { functions } from '../lib/firebase';
import { readDealCandidates, type TenantDealCandidate } from '../utils/partnerReferrals';

/**
 * "My referral program": the desk of a referral program this tenant operates
 * (docs/plans/2026-10-23-WEBSITE-FORMS-PLATFORM.md §16), worked from inside
 * VALUTTEN with the caller's own tenant ID token.
 *
 *   - Desk calls: the loyalty-codebase partner* callables (same Firebase
 *     project and region as this app's functions instance, so they are called
 *     by name). The tenant door requires `partnerId` on every call; the
 *     server checks operatingCompanyId === claims.companyId, the add-on and
 *     the role's capability on each one.
 *   - Loan connection (crm): deal candidates come from the client-functions
 *     callable tenantGetPartnerReferralDealCandidates (resolveBrokerScope,
 *     matched on the referral's own client name, no free-text search, audited,
 *     rate limited). Linking accepts only a deal that call showed this caller
 *     in the last 30 minutes.
 *
 * Money is integer cents in every payload; nothing here formats or converts it.
 */

type Call = (name: string, data: Record<string, unknown>) => Promise<unknown>;

/** The default transport: one httpsCallable per name on this app's functions instance. */
export const callByName: Call = async (name, data) => (await httpsCallable(functions, name)(data)).data;

/** Candidate row (client-functions) to the desk's DealCandidate. The callable returns no loan value by construction. */
export function toDeskDealCandidate(c: TenantDealCandidate): DealCandidate {
  return {
    dealId: c.dealId,
    provider: c.provider,
    borrowerName: c.borrowerName,
    dealName: null,
    status: c.status,
    customStatusName: null,
    totalLoanAmountCents: null,
    dateSettled: c.settlementMonth,
    adviserName: c.adviserName,
  };
}

/** Raw candidates for one referral, with the grant expiry (ms) the link must happen before. */
export async function fetchDealCandidates(
  partnerId: string,
  referralId: string,
  call: Call = callByName
): Promise<{ candidates: TenantDealCandidate[]; grantExpiresAtMs: number | null }> {
  const res = (await call('tenantGetPartnerReferralDealCandidates', { partnerId, referralId })) as {
    grantExpiresAtMs?: unknown;
  } | null;
  const exp = res?.grantExpiresAtMs;
  return {
    candidates: readDealCandidates(res),
    grantExpiresAtMs: typeof exp === 'number' && Number.isFinite(exp) ? exp : null,
  };
}

/**
 * The DeskApi for one program on the tenant door. Every call carries
 * `partnerId`; responses pass through untouched except where the desk's
 * shapes differ from the wire (noted per method).
 */
export function createTenantDeskApi(partnerId: string, call: Call = callByName): DeskApi {
  // The desk's method inputs are plain objects; the server validates each one.
  const c = <T>(name: string, input?: object): Promise<T> =>
    call(name, { ...(input ?? {}), partnerId }) as Promise<T>;
  return {
    listReferrals: () => c('partnerListReferrals'),
    getReferral: (input) => c('partnerGetReferral', input),
    advanceReferral: (input) => c('partnerAdvanceReferral', input),
    linkReferralToMember: (input) => c('partnerLinkReferralToMember', input),
    inviteReferrer: (input) => c('partnerInviteReferrer', input),
    rejectReferral: (input) => c('partnerRejectReferral', input),
    resolveDuplicate: (input) => c('partnerResolveDuplicate', input),

    getFeeSchedule: (input) => c('partnerGetFeeSchedule', input),
    updateFeeSchedule: (input) => c('partnerUpdateFeeSchedule', input),
    listFeeSchedules: () => c('partnerListFeeSchedules'),
    upsertFeeScheduleVariant: (input) => c('partnerUpsertFeeScheduleVariant', input),
    deleteFeeScheduleVariant: (input) => c('partnerDeleteFeeScheduleVariant', input),
    setMemberFeeSchedule: (input) => c('partnerSetMemberFeeSchedule', input),

    listMembers: () => c('partnerListMembers'),
    inviteMember: (input) => c('partnerInviteMember', input),
    updateMember: (input) => c('partnerUpdateMember', input),
    resendInvite: (input) => c('partnerResendInvite', input),
    cancelInvite: (input) => c('partnerCancelInvite', input),
    listApplications: () => c('partnerListApplications'),
    decideApplication: (input) => c('partnerDecideApplication', input),

    listLedger: () => c('partnerListLedger'),
    markLedgerPaid: (input) => c('partnerMarkLedgerPaid', input),
    listGiftCards: () => c('partnerListGiftCards'),
    issueGiftCard: (input) => c('partnerIssueGiftCard', input),
    cancelGiftCardRequest: (input) => c('partnerCancelGiftCardRequest', input),
    requestClientRewardGiftCard: (input) => c('partnerRequestClientRewardGiftCard', input),
    getFloat: () => c('partnerGetFloat'),
    recordFloatTopUp: (input) => c('partnerRecordFloatTopUp', input),

    getPlatformSettings: () => c('partnerGetPlatformSettings'),
    updatePlatformSettings: (input) => c('partnerUpdatePlatformSettings', input),

    crm: {
      listDealCandidates: async ({ referralId }) => ({
        deals: (await fetchDealCandidates(partnerId, referralId, call)).candidates.map(toDeskDealCandidate),
      }),
      // Wire: { ok, crmLink: { provider, dealId } }; the desk reloads the referral for the link itself.
      linkDeal: async ({ referralId, dealId }) => {
        const res = await c<{ ok?: unknown }>('partnerLinkCrmDeal', { referralId, dealId });
        return { ok: res?.ok === true };
      },
      unlinkDeal: async ({ referralId }) => {
        const res = await c<{ ok?: unknown; changed?: unknown }>('partnerUnlinkCrmDeal', { referralId });
        return { ok: res?.ok === true, changed: res?.changed === true };
      },
    },
  };
}

// ─── The program's words and promises ───────────────────────────────────────

export interface DeskProgramDetails {
  loading: boolean;
  vocabulary: PartnerVocabulary;
  firstContactHours: number | null;
  rewards: RewardsConfig | null;
  brandColor: string | null;
}

/**
 * The program's public profile (vocabulary, first-contact promise, brand
 * colour) through partnerGetPublicProfile by its id. A failure falls back to
 * the default words and no overdue rule: the desk itself still works.
 */
export function useDeskProgramDetails(partnerId: string, call: Call = callByName): DeskProgramDetails {
  const [profile, setProfile] = useState<{ id: string; value: unknown } | null>(null);

  useEffect(() => {
    let active = true;
    call('partnerGetPublicProfile', { slug: partnerId })
      .then((value) => {
        if (active) setProfile({ id: partnerId, value });
      })
      .catch(() => {
        if (active) setProfile({ id: partnerId, value: null });
      });
    return () => {
      active = false;
    };
  }, [partnerId, call]);

  return useMemo(() => {
    const value = profile?.id === partnerId ? profile.value : null;
    const brand = (value as { brandColor?: unknown } | null)?.brandColor;
    return {
      loading: profile?.id !== partnerId,
      vocabulary: vocabularyOf(value as { vocabulary?: Partial<PartnerVocabulary> | null } | null),
      firstContactHours: readFirstContactHours(value),
      rewards: readRewards(value),
      brandColor: typeof brand === 'string' ? brand : null,
    };
  }, [profile, partnerId]);
}
